import { useCallback, useState, useEffect } from 'react';
import * as React from 'react';
import { useSlate, useFocused } from 'slate-react';
import styled from 'styled-components';
import { Editor, Element } from 'slate';
import Button from '@sdk/components/primitives/Button';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import View from '@sdk/components/primitives/View';

import LinkSvg from '@web/assets/svg/UI/link.svg';
import ActiveLinkSvg from '@web/assets/svg/UI/link-active.svg';

import { addMark, isLinkActive } from '../helpers/utils';

const LinkButton = styled(Button)`
  background-color: transparent;
  margin-right: 3px;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.hoverBrand};
  }
`;

type Props = {
  modalState: ModalState;
  onLinkClick: (url: string, fromToolbar?: boolean) => void;
};

const LinkToolbarButton: React.FC<React.PropsWithChildren<Props>> = ({
  modalState,
  onLinkClick,
}) => {
  const editor = useSlate();
  const focused = useFocused();
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    if (trigger) {
      onLinkClick('', true);
    }
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = useCallback(
    (event): void => {
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();

      const selectedText = editor.selection && Editor.string(editor, editor.selection);
      if (!selectedText && !isLinkActive(editor)) {
        return;
      }

      if (!isLinkActive(editor)) {
        addMark(editor, 'highlight');
        setTrigger(trigger + 1);
      }

      const [link] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
      });

      const linkNode = link?.[0];
      if (Element.isElement(linkNode) && linkNode.type === 'link') {
        onLinkClick(linkNode.link, true);
      }
    },
    [onLinkClick, editor, trigger]
  );

  return (
    <View>
      <LinkButton disabled={!focused} onMouseDown={openModal}>
        {modalState.isVisible || isLinkActive(editor) ? <ActiveLinkSvg /> : <LinkSvg />}
      </LinkButton>
    </View>
  );
};

export default LinkToolbarButton;
